import { Link } from "react-router-dom";
import { CircleX, Hourglass, ShieldX, TriangleAlert, Wrench } from "lucide-react";
import { Skeleton } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import { StatusDot, type Tone } from "@/components/ui/status";
import { cn } from "@/lib/utils";

export function LoadingState({ rows = 4 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading" className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} shimmer className="h-11 w-full rounded-md" />
      ))}
    </div>
  );
}

export function LoadingTable({ rows = 4, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div role="status" aria-label="Loading table" className="overflow-hidden rounded-lg border border-line bg-surface">
      <div className="flex gap-6 border-b border-line bg-bg px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} shimmer className="h-2.5 w-16 rounded" />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, row) => (
        <div key={row} className="flex gap-6 border-b border-line px-4 py-3.5 last:border-b-0">
          {Array.from({ length: cols }).map((_, col) => (
            <Skeleton key={col} shimmer className="h-3 w-20 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  description,
  details,
  retryLabel = "Try again",
  onRetry,
}: {
  title?: string;
  description?: string;
  details?: string;
  retryLabel?: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 rounded-lg border border-danger-border bg-danger-soft px-6 py-12 text-center">
      <CircleX aria-hidden className="size-10 text-danger" />
      <div className="text-base font-bold text-ink">{title}</div>
      {description && <p className="max-w-[420px] text-sm leading-relaxed text-muted">{description}</p>}
      {details && <p className="max-w-[420px] font-mono text-xs text-danger">{details}</p>}
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export function MaintenanceState({
  title = "Scheduled maintenance",
  description,
  endsAt,
  rows,
  actions,
  className,
}: {
  title?: string;
  description?: string;
  endsAt?: string;
  rows?: { k: string; v: React.ReactNode }[];
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-warning-border bg-warning-soft px-6 py-8",
        className
      )}
    >
      <div className="flex flex-col items-center gap-3 text-center">
        <Wrench aria-hidden className="size-10 text-warning" />
        <div className="text-base font-bold text-ink">{title}</div>
        {description && <p className="max-w-[520px] text-sm leading-relaxed text-muted">{description}</p>}
        {endsAt && <p className="text-xs font-semibold text-warning">Expected to resume {endsAt}</p>}
      </div>
      {rows && rows.length > 0 && (
        <div className="overflow-hidden rounded-md border border-warning-border bg-surface">
          {rows.map((row, i) => (
            <div
              key={`${row.k}-${i}`}
              className={cn(
                "grid grid-cols-[160px_1fr] gap-3 px-4 py-2.5 text-sm",
                i < rows.length - 1 && "border-b border-line"
              )}
            >
              <span className="text-muted">{row.k}</span>
              <span className="font-semibold text-ink">{row.v}</span>
            </div>
          ))}
        </div>
      )}
      {actions && <div className="flex flex-wrap justify-center gap-3">{actions}</div>}
    </div>
  );
}

export function PermissionDeniedState({
  title = "Access required",
  description,
  icon,
  details,
  action,
  footnote,
}: {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  details?: { k: string; v: string }[];
  action?: React.ReactNode;
  footnote?: React.ReactNode;
}) {
  return (
    <div role="alert" className="flex flex-col items-center gap-3 rounded-lg border border-line bg-surface px-6 py-12 text-center">
      <div className="grid size-16 place-items-center rounded-full border border-line bg-bg text-2xl">
        {icon ?? <ShieldX aria-hidden className="size-8 text-danger" />}
      </div>
      <div className="text-base font-bold text-ink">{title}</div>
      {description && <p className="max-w-[460px] text-sm leading-relaxed text-muted">{description}</p>}
      {details && details.length > 0 && (
        <div className="w-full max-w-[480px] rounded-md border border-info-border bg-info-soft p-4 text-left">
          <div className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-info">Access details</div>
          <div className="flex flex-col gap-2">
            {details.map((d) => (
              <div key={d.k} className="flex gap-3 text-sm">
                <span className="w-[120px] shrink-0 text-muted">{d.k}</span>
                <span className="font-semibold text-ink">{d.v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {action && <div className="mt-1">{action}</div>}
      {footnote && <div className="text-xs text-muted">{footnote}</div>}
    </div>
  );
}

export function StaleState({
  title = "Data may be stale",
  description,
  lastSyncedAt,
  action,
  className,
}: {
  title?: string;
  description?: string;
  lastSyncedAt?: string;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div role="alert" className={cn("mb-4 flex flex-col gap-3 rounded-md border border-warning-border bg-warning-soft px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between", className)}>
      <div className="flex items-start gap-2.5">
        <Hourglass aria-hidden className="mt-0.5 size-4 shrink-0 text-warning" />
        <div className="min-w-0">
          <div className="text-xs font-bold text-warning">{title}</div>
          {description && <p className="mt-0.5 text-xs text-warning">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {lastSyncedAt && <span className="whitespace-nowrap text-xs text-warning">{lastSyncedAt}</span>}
        {action}
      </div>
    </div>
  );
}

export function OutcomeUnknownState({
  title = "Outcome unresolved",
  description,
  traceId,
  actions,
  className,
}: {
  title?: string;
  description: string;
  traceId?: string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <section role="status" className={cn("rounded-lg border border-line bg-console-bg p-5", className)}>
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs font-semibold uppercase tracking-wider text-console-warning">{title}</span>
        <TriangleAlert aria-hidden className="size-4 shrink-0 text-console-warning" />
      </div>
      <p className="mt-2 font-mono text-[13px] leading-relaxed text-console-text">{description}</p>
      {traceId && <p className="mt-3 font-mono text-[11px] text-console-muted">Trace ID {traceId}</p>}
      {actions && <div className="mt-4 flex flex-wrap gap-2">{actions}</div>}
    </section>
  );
}

/**
 * Full-page error specimen (SYS-04). Shared by the 503/404 specimens, the global
 * `not-found.tsx` and the global `error.tsx` boundary.
 */
export function FullPageError({
  code,
  title,
  description,
  incident,
  actions,
  statuses,
  codeTone = "primary",
  homeHref,
  homeLabel,
}: {
  code: string;
  title: string;
  description: string;
  incident?: string;
  actions?: React.ReactNode;
  statuses?: { label: string; tone: Tone }[];
  codeTone?: "primary" | "warning" | "danger";
  homeHref?: string;
  homeLabel?: string;
}) {
  return (
    <div className="flex flex-col items-center rounded-lg border border-line bg-surface px-6 py-14 text-center">
      <div
        className={cn(
          "font-mono text-[64px] font-bold leading-none",
          codeTone === "primary" && "text-primary",
          codeTone === "warning" && "text-warning",
          codeTone === "danger" && "text-danger"
        )}
      >
        {code}
      </div>
      <div className="mt-3 text-xl font-extrabold text-ink">{title}</div>
      <p className="mt-2 max-w-[460px] text-sm leading-relaxed text-muted">{description}</p>
      {incident && <p className="mt-2 font-mono text-xs text-muted">{incident}</p>}
      {(actions || homeHref) && (
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          {homeHref && (
            <Link
              to={homeHref}
              className="inline-flex min-h-[36px] items-center justify-center rounded-sm bg-primary px-3.5 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-hover"
            >
              {homeLabel ?? "Go home"}
            </Link>
          )}
          {actions}
        </div>
      )}
      {statuses && statuses.length > 0 && (
        <div className="mt-6 flex flex-wrap justify-center gap-4">
          {statuses.map((s) => (
            <span key={s.label} className="inline-flex items-center gap-1.5 text-xs text-muted">
              <StatusDot tone={s.tone} /> {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
